import { useState } from 'react'
import { Scissors, Eye, EyeOff, Lock, Phone, AlertCircle } from 'lucide-react'
import { verifyAccessCode, storageMode } from '../supabase'

interface AccessScreenProps {
  onAuthenticated: (code: string) => void
}

export default function AccessScreen({ onAuthenticated }: AccessScreenProps) {
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleCodeChange = (val: string) => {
    // On conserve la saisie telle quelle (copier-coller intact)
    // La normalisation en majuscules se fait uniquement à la soumission
    setCode(val)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const trimmedCode = code.trim().toUpperCase()
    if (!trimmedCode) {
      setError('Veuillez saisir votre code d\'accès.')
      return
    }
    if (!trimmedCode.startsWith('SALON-')) {
      setError('Le code doit commencer par SALON- (ex : SALON-Ab3Xyz9K).')
      return
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.')
      return
    }

    setLoading(true)
    const result = await verifyAccessCode(trimmedCode, password)
    setLoading(false)

    if (result.success) {
      onAuthenticated(trimmedCode)
    } else {
      setError(result.error ?? 'Erreur d\'authentification.')
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #1A3C6E 0%, #0D2247 100%)' }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <Scissors className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Loris Hug Innovation</h1>
          <p className="text-white/70 mt-1 text-sm">Calculateur de Seuil de Rentabilité</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header card */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: '#1A3C6E' }} />
              <span className="font-semibold text-sm" style={{ color: '#1A3C6E' }}>
                Accès sur invitation
              </span>
            </div>
            {storageMode === 'local' && (
              <span className="text-xs px-2 py-1 rounded-full font-medium"
                style={{ background: '#FFFBEB', color: '#92400E' }}>
                Mode local
              </span>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Champ code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code d'accès
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="SALON-AB3XYZ9K"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent tracking-wider"
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-gray-400 mt-1">
                Collez exactement le code reçu (ex : <span className="font-mono">SALON-AB3XYZ9K</span>)
              </p>
            </div>

            {/* Champ mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe
                <span className="text-gray-400 font-normal ml-1 text-xs">
                  (1ère visite : choisissez le vôtre)
                </span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Erreur */}
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-xl text-sm"
                style={{ background: '#FFF5F5', color: '#C62828', border: '1px solid #FEE2E2' }}>
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Bouton */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-60"
              style={{ background: '#1A3C6E' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10"
                      stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Vérification...
                </span>
              ) : (
                'Accéder à l\'outil →'
              )}
            </button>
          </form>

          {/* Footer card */}
          <div className="px-6 pb-6 space-y-3">
            <div className="rounded-xl p-4 text-sm"
              style={{ background: '#F0F7FF', border: '1px solid #DBEAFE' }}>
              <p className="font-medium mb-1" style={{ color: '#1A3C6E' }}>
                Vous n'avez pas de code ?
              </p>
              <p className="text-gray-500 flex items-center gap-1 text-xs">
                <Phone className="w-3 h-3" />
                Contactez Loris Hug Innovation pour recevoir votre invitation.
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-white/40 text-xs mt-6">
          © 2026 Loris Hug Innovation — Outil sur invitation
        </p>
      </div>
    </div>
  )
}
